import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  FileText, 
  Search,
  Download,
  Printer,
  Eye,
  Loader2,
  Calendar,
  Package,
  Truck,
  ArrowRight,
  ArrowLeft,
  RefreshCw,
  Filter,
  Tag
} from "lucide-react";
import { toast } from "sonner";
import Pagination from "../components/Pagination";
import ServiceBadge from "../components/ServiceBadge";

const WaybillsPage = () => {
  const [waybills, setWaybills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [directionFilter, setDirectionFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [senderPlace, setSenderPlace] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if we're on the Returns/Collections page
  const isReturnsPage = location.pathname === "/collections";

  useEffect(() => {
    fetchWaybills();
    fetchSenderSettings();
  }, []);
  
  // Auto-set filter to "incoming" when on returns page
  useEffect(() => {
    if (isReturnsPage) {
      setDirectionFilter("incoming");
    }
  }, [isReturnsPage]);

  const fetchWaybills = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/waybills`);
      setWaybills(response.data);
    } catch (error) {
      console.error("Error fetching waybills:", error);
      toast.error("Failed to load waybills");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSenderSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data?.ff_place_id) {
        setSenderPlace(response.data.ff_place_id);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const syncFromFirstFreight = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/ff/sync-history?days=90`);
      const data = response.data;
      if (data.success) {
        toast.success(`Synced ${data.waybills_synced} waybills from courier`);
        fetchWaybills(); // Refresh the list
      } else {
        toast.error(data.error || "Sync failed");
      }
    } catch (error) {
      console.error("Sync error:", error);
      toast.error("Failed to sync from courier");
    } finally {
      setSyncing(false);
    }
  };

  const filteredWaybills = useMemo(() => {
    const q = searchTerm.toLowerCase();
    
    // First filter by search term
    let filtered = waybills.filter(w => 
      w.waybill_number?.toLowerCase().includes(q) ||
      w.order_number?.toLowerCase().includes(q) ||
      w.reference?.toLowerCase().includes(q) ||
      w.recipient?.name?.toLowerCase().includes(q) ||
      w.status?.toLowerCase().includes(q) ||
      w.destination?.toLowerCase().includes(q)
    );
    
    // Then filter by direction
    if (directionFilter === "outgoing") {
      // Outgoing: Our warehouse is the sender (origin)
      // Reference starts with "FF-" (standard waybills from orders)
      filtered = filtered.filter(w => 
        w.reference?.startsWith("FF-") || 
        w.type === "outgoing" ||
        !w.reference?.startsWith("RET-")
      );
    } else if (directionFilter === "incoming") {
      // Incoming/Returns: Our warehouse is the destination
      // Reference starts with "RET-" (returns)
      filtered = filtered.filter(w => 
        w.reference?.startsWith("RET-") || 
        w.type === "incoming" ||
        w.type === "return"
      );
    }
    // "all" shows everything
    
    return filtered;
  }, [waybills, searchTerm, directionFilter]);

  // Reset page when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, directionFilter]);

  // Paginated waybills
  const paginatedWaybills = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredWaybills.slice(startIndex, startIndex + pageSize);
  }, [filteredWaybills, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredWaybills.length / pageSize);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const downloadPDF = async (waybillId, waybillNumber) => {
    try {
      const response = await axios.get(`${API}/waybills/${waybillId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `waybill-${waybillNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("A4 Waybill downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const downloadTextWaybill = async (waybillId, waybillNumber) => {
    try {
      const response = await axios.get(`${API}/waybills/${waybillId}/text-waybill`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `blank-waybill-${waybillNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Blank waybill downloaded");
    } catch (error) {
      toast.error("Failed to download blank waybill");
    }
  };

  const downloadLabels = async (waybillId, waybillNumber) => {
    try {
      const response = await axios.get(`${API}/waybills/${waybillId}/labels`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `labels-${waybillNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Parcel stickers downloaded");
    } catch (error) {
      toast.error("No parcel stickers available");
    }
  };

  const printWaybill = async (waybillId) => {
    try {
      const response = await axios.get(`${API}/waybills/${waybillId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url);
      printWindow.onload = () => {
        printWindow.print();
      };
    } catch (error) {
      toast.error("Failed to print waybill");
    }
  };

  const getStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('checked in'))
      return <Badge className="bg-green-100 text-green-800 border-green-200">Checked In</Badge>;
    if (s.includes('collected'))
      return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Collected</Badge>;
    if (s.includes('uncollected'))
      return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Uncollected</Badge>;
    if (s.includes('cancelled'))
      return <Badge className="bg-red-100 text-red-800 border-red-200">Cancelled</Badge>;
    if (s.includes('missed'))
      return <Badge className="bg-red-100 text-red-800 border-red-200">Missed</Badge>;
    if (s.includes('unassigned'))
      return <Badge className="bg-slate-100 text-slate-800 border-slate-200">Unassigned</Badge>;
    if (s === 'submitted')
      return <Badge className="bg-green-100 text-green-800 border-green-200">Submitted</Badge>;
    if (s === 'local')
      return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Local</Badge>;
    return <Badge variant="outline">{status}</Badge>;
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            {isReturnsPage ? "Returns" : "Waybills"}
          </h1>
          <p className="text-slate-500 mt-1">
            {isReturnsPage 
              ? "View incoming shipments and returns to your warehouse" 
              : "View and manage your waybills"}
          </p>
        </div>
        <Button 
          onClick={syncFromFirstFreight} 
          disabled={syncing}
          variant="outline"
          className="border-green-300 text-green-700 hover:bg-green-50"
        >
          {syncing ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          {syncing ? "Syncing..." : "Refresh from courier"}
        </Button>
      </div>

      {/* Search & Filter */}
      <Card className="border-slate-200 shadow-sm mb-6">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by waybill #, order #, reference or recipient..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-slate-200"
                data-testid="waybills-search"
              />
            </div>
            {!isReturnsPage && (
              <Select value={directionFilter} onValueChange={setDirectionFilter}>
                <SelectTrigger className="w-[180px] border-slate-200">
                  <Filter className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Waybills</SelectItem>
                  <SelectItem value="outgoing">
                    <span className="flex items-center gap-2">
                      <ArrowRight className="w-3 h-3" /> Outgoing
                    </span>
                  </SelectItem>
                  <SelectItem value="incoming">
                    <span className="flex items-center gap-2">
                      <ArrowLeft className="w-3 h-3" /> Incoming / Returns
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Waybills List */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#16a34a]" />
            </div>
          ) : filteredWaybills.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No waybills found</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => navigate("/orders")}
              >
                Go to Orders
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-slate-50 text-xs font-medium text-slate-500 uppercase tracking-wider">
                <div className="col-span-2">Waybill #</div>
                <div className="col-span-2">Sender</div>
                <div className="col-span-3">Destination</div>
                <div className="col-span-2">Date</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-2">Actions</div>
              </div>
              
              {/* Table Rows */}
              {paginatedWaybills.map((waybill, index) => (
                <div 
                  key={waybill.id || waybill.waybill_number} 
                  className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-slate-50/50 transition-colors animate-slide-in"
                  style={{ animationDelay: `${index * 0.03}s` }}
                  data-testid={`waybill-row-${waybill.waybill_number}`}
                >
                  <div className="col-span-2">
                    <p className="font-mono font-semibold text-[#16a34a]">{waybill.waybill_number}</p>
                    {waybill.collection_date && (
                      <p className="text-xs text-slate-500">{waybill.collection_date}</p>
                    )}
                    {waybill.service_type && (
                      <ServiceBadge
                        service={waybill.service_type}
                        size="xs"
                        className="mt-1"
                        testId={`waybill-service-${waybill.waybill_number}`}
                      />
                    )}
                  </div>
                  <div className="col-span-2">
                    {waybill.order_number && waybill.order_id ? (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-slate-700 hover:text-[#16a34a] p-0 h-auto"
                        onClick={() => navigate(`/orders/${waybill.order_id}`)}
                      >
                        <Package className="w-4 h-4 mr-1" />
                        #{waybill.order_number}
                      </Button>
                    ) : waybill.order_number ? (
                      <div className="flex items-center text-slate-700">
                        <Package className="w-4 h-4 mr-1 text-slate-400" />
                        <span className="text-sm">#{waybill.order_number}</span>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm font-medium text-slate-700 truncate">{waybill.sender?.name || waybill.sender_company || '-'}</p>
                        <p className="text-xs text-slate-500 truncate">{waybill.sender?.city || ''}</p>
                      </div>
                    )}
                  </div>
                  <div className="col-span-3">
                    <p className="font-medium text-slate-900 text-sm">{waybill.recipient?.name || waybill.destination || '-'}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {waybill.reference ? waybill.reference : (waybill.branch || '')}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      {waybill.created_at?.substring(0, 10)}
                    </div>
                  </div>
                  <div className="col-span-1">
                    {getStatusBadge(waybill.status)}
                  </div>
                  <div className="col-span-2 flex items-center gap-1">
                    {waybill.source !== 'first_freight_api' && (
                      <>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => downloadPDF(waybill.id, waybill.waybill_number)}
                          className="text-slate-600 hover:text-[#4A7C4E] p-1"
                          data-testid={`download-a4-${waybill.waybill_number}`}
                          title="Normal A4 Waybill"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => downloadLabels(waybill.id, waybill.waybill_number)}
                          className="text-slate-600 hover:text-[#16a34a] p-1"
                          data-testid={`download-labels-${waybill.waybill_number}`}
                          title="Parcel Stickers"
                        >
                          <Tag className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    {waybill.source === 'first_freight_api' && (
                      <a
                        href={`https://firstfreight.pperfect.com/?w=${waybill.waybill_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-[#16a34a] hover:underline flex items-center gap-1"
                      >
                        <Eye className="w-4 h-4" />
                        Track
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {filteredWaybills.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredWaybills.length}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WaybillsPage;
